import { useTranslation } from 'react-i18next'

import { Badge } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import type { I18n, Task, TaskPriority, TaskStatus } from '../types'

/**
 * La liste des tâches — deux rendus, pas un tableau rétréci.
 *
 * Sur grand écran, un tableau : c'est ce qu'on parcourt des yeux colonne par
 * colonne pour comparer des échéances. Sur téléphone, des cartes : le même
 * tableau comprimé obligerait à faire défiler latéralement pour lire qui est
 * responsable, et personne ne le fait — on renonce et on demande à voix haute.
 *
 * Les deux rendus montrent les mêmes sept informations. Cacher une colonne sur
 * mobile reviendrait à décider que le téléphone sert à consulter et l'ordinateur
 * à travailler, alors que sur un événement c'est exactement l'inverse.
 */
export function TaskListView({
  tasks,
  statuses,
  priorities,
  categoryNames,
  label,
  busy,
  onMove,
  onOpen,
}: {
  tasks: Task[]
  statuses: TaskStatus[]
  priorities: TaskPriority[]
  categoryNames: Map<string, string>
  label: (value: I18n) => string
  busy: boolean
  onMove: (id: string, statusId: string) => void
  onOpen: (id: string) => void
}) {
  const { t, i18n } = useTranslation()
  const priorityNames = new Map(priorities.map((row) => [row.id, label(row.name_i18n)]))
  const priorityLevels = new Map(priorities.map((row) => [row.id, row.level]))
  const statusNames = new Map(statuses.map((row) => [row.id, label(row.name_i18n)]))

  const when = (value: string | null) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : null

  if (tasks.length === 0) {
    return (
      <p className="rounded-lg border border-line-subtle px-4 py-8 text-sm text-content-muted">
        {t('admin.project.noTasks')}
      </p>
    )
  }

  return (
    <>
      {/* ── Grand écran ────────────────────────────────────────── */}
      <div className="hidden overflow-x-auto rounded-lg border border-line-subtle lg:block">
        <table className="w-full min-w-[60rem] text-sm">
          <thead>
            <tr className="border-b border-line-subtle bg-bg-elevated text-left">
              <Th>{t('admin.project.task')}</Th>
              <Th>{t('admin.project.category')}</Th>
              <Th>{t('admin.project.createdBy')}</Th>
              <Th>{t('admin.project.owner')}</Th>
              <Th>{t('admin.project.priority')}</Th>
              <Th>{t('admin.project.due')}</Th>
              <Th>{t('admin.project.progress')}</Th>
              <Th>{t('admin.project.status')}</Th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id} className="border-b border-line-subtle/50">
                <td className="px-3 py-2.5">
                  <span className="mr-2 font-mono text-caption text-content-muted">
                    {task.reference}
                  </span>
                  <button
                    type="button"
                    className="text-left text-content-primary hover:text-champagne-300"
                    onClick={() => onOpen(task.id)}
                  >
                    {task.title}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-content-secondary">
                  {task.category_id ? (categoryNames.get(task.category_id) ?? '—') : '—'}
                </td>
                <td className="px-3 py-2.5 text-content-secondary">
                  {task.created_by_label ?? '—'}
                </td>
                <td className="px-3 py-2.5">
                  <span className="text-content-secondary">{task.owner_label ?? '—'}</span>
                  {task.assignee_labels.length > 0 ? (
                    <span className="block text-caption text-content-muted">
                      + {task.assignee_labels.join(', ')}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2.5">
                  <PriorityTag
                    name={task.priority_id ? priorityNames.get(task.priority_id) : undefined}
                    level={task.priority_id ? priorityLevels.get(task.priority_id) : undefined}
                  />
                </td>
                <td className="px-3 py-2.5">
                  {task.due_at ? (
                    <span
                      className={
                        task.is_overdue ? 'text-state-danger' : 'text-content-secondary'
                      }
                    >
                      {when(task.due_at)}
                    </span>
                  ) : (
                    <span className="text-content-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <Progress value={task.progress} />
                </td>
                <td className="px-3 py-2.5">
                  <StatusPicker
                    task={task}
                    statuses={statuses}
                    label={label}
                    busy={busy}
                    onMove={onMove}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Téléphone et tablette ──────────────────────────────── */}
      <div className="grid gap-2 lg:hidden">
        {tasks.map((task) => (
          <article
            key={task.id}
            className={cn(
              'rounded-lg border bg-bg-elevated px-4 py-3.5',
              task.is_overdue ? 'border-state-danger/50' : 'border-line-subtle',
            )}
          >
            {/* Priorité et chantier en tête : ce sont les deux repères qui
                permettent de balayer une liste sans la lire. */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <PriorityTag
                name={task.priority_id ? priorityNames.get(task.priority_id) : undefined}
                level={task.priority_id ? priorityLevels.get(task.priority_id) : undefined}
              />
              <span className="text-caption uppercase tracking-wide text-content-muted">
                {task.category_id ? (categoryNames.get(task.category_id) ?? '—') : '—'}
              </span>
            </div>

            {/* Le titre entier, sur plusieurs lignes s'il le faut : le tronquer
                sur un écran étroit rendrait la moitié des tâches
                indistinguables (« Préparer signalétique… »). */}
            <button
              type="button"
              onClick={() => onOpen(task.id)}
              className="mt-2 block w-full text-left"
            >
              <span className="block text-content-primary">{task.title}</span>
              <span className="mt-0.5 block font-mono text-caption text-content-muted">
                {task.reference}
              </span>
            </button>

            <dl className="mt-3 grid gap-1 text-caption">
              <Line
                term={t('admin.project.owner')}
                value={task.owner_label ?? t('admin.project.noOwner')}
                strong={Boolean(task.owner_label)}
              />
              {task.assignee_labels.length > 0 ? (
                <Line
                  term={t('admin.project.assignees')}
                  value={task.assignee_labels.join(', ')}
                />
              ) : null}
              <Line
                term={t('admin.project.createdBy')}
                value={task.created_by_label ?? '—'}
              />
              {task.due_at ? (
                <Line
                  term={t('admin.project.due')}
                  value={when(task.due_at) ?? ''}
                  danger={task.is_overdue}
                />
              ) : null}
            </dl>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {task.is_overdue ? (
                <Badge tone="ember">{t('admin.project.overdue')}</Badge>
              ) : null}
              <span className="min-w-0 flex-1">
                <Progress value={task.progress} />
              </span>
              <span className="shrink-0 text-caption tabular-nums text-content-muted">
                {statusNames.get(task.status_id) ?? ''}
              </span>
            </div>

            <div className="mt-3">
              <StatusPicker
                task={task}
                statuses={statuses}
                label={label}
                busy={busy}
                onMove={onMove}
                full
              />
            </div>
          </article>
        ))}
      </div>
    </>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-normal text-content-muted">{children}</th>
}

function Line({
  term,
  value,
  strong,
  danger,
}: {
  term: string
  value: string
  strong?: boolean
  danger?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="shrink-0 text-content-muted">{term} :</dt>
      <dd
        className={cn(
          'min-w-0',
          danger
            ? 'text-state-danger'
            : strong
              ? 'text-champagne-300'
              : 'text-content-secondary',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

/** La priorité par sa **gravité**, pas par son libellé — qui est renommable. */
function PriorityTag({
  name,
  level,
}: {
  // `exactOptionalPropertyTypes` : « absent » et « présent mais indéfini »
  // sont deux choses différentes pour ce projet, et une lecture de `Map`
  // rend la seconde.
  name: string | undefined
  level: number | undefined
}) {
  if (!name) return <span className="text-caption text-content-muted">—</span>
  const critical = (level ?? 0) >= 90
  const high = (level ?? 0) >= 70
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide',
        critical
          ? 'border-state-danger/50 text-state-danger'
          : high
            ? 'border-champagne-400/50 text-champagne-300'
            : 'border-line text-content-muted',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          critical ? 'bg-state-danger' : high ? 'bg-champagne-400' : 'bg-content-muted',
        )}
      />
      {name}
    </span>
  )
}

function Progress({ value }: { value: number }) {
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-bg-overlay">
        <span
          className="block h-full rounded-full bg-champagne-400"
          style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
        />
      </span>
      <span className="shrink-0 text-caption tabular-nums text-content-muted">
        {value} %
      </span>
    </span>
  )
}

/**
 * Changer de colonne.
 *
 * Un `<select>` natif plutôt qu'un glisser-déposer : la liste vit dans une page
 * qui défile, et traîner une carte sur trois écrans de haut se termine
 * invariablement par un lâcher au mauvais endroit — surtout au doigt, debout,
 * dans un couloir. Le geste appelle vraiment le serveur ; il n'y a pas de
 * déplacement purement visuel.
 */
function StatusPicker({
  task,
  statuses,
  label,
  busy,
  onMove,
  full,
}: {
  task: Task
  statuses: TaskStatus[]
  label: (value: I18n) => string
  busy: boolean
  onMove: (id: string, statusId: string) => void
  full?: boolean
}) {
  const { t } = useTranslation()
  return (
    <select
      aria-label={t('admin.project.moveTo')}
      value={task.status_id}
      disabled={busy}
      onChange={(event) => onMove(task.id, event.target.value)}
      className={cn(
        'rounded-md border border-line bg-bg-base px-2 text-caption text-content-secondary',
        full ? 'h-10 w-full' : 'py-1',
      )}
    >
      {statuses.map((status) => (
        <option key={status.id} value={status.id}>
          {label(status.name_i18n)}
        </option>
      ))}
    </select>
  )
}
